/* PWAInstallBanner.jsx
   Handles install prompt for Chrome/Edge (beforeinstallprompt)
   and shows manual instructions for iOS Safari.
   Drop into App.jsx: <PWAInstallBanner /> anywhere in the render tree.
*/

import { useState, useEffect } from 'react';

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed (running in standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return; // iOS standalone

    // Don't show if user previously dismissed
    const wasDismissed = localStorage.getItem('phs_pwa_dismissed');
    if (wasDismissed) return;

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isIOS && isSafari) {
      // Show iOS instructions after a short delay on first visit
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Chrome/Edge: wait for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      setShowIOSGuide(true);
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('phs_pwa_dismissed', '1');
  };

  if (!showBanner || dismissed) return null;

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <>
      {/* Install banner */}
      <div style={styles.banner}>
        <div style={styles.left}>
          <img
            src="/icons/icon-72x72.png?v=2"
            alt="Your Peak Stack"
            style={styles.icon}
          />
          <div>
            <div style={styles.title}>Install Your Peak Stack</div>
            <div style={styles.subtitle}>Add to home screen for quick access</div>
          </div>
        </div>
        <div style={styles.actions}>
          {isIOS ? (
            <button style={styles.installBtn} onClick={() => setShowIOSGuide(true)}>
              How to install
            </button>
          ) : (
            <button style={styles.installBtn} onClick={handleInstall}>
              Install
            </button>
          )}
          <button style={styles.dismissBtn} onClick={handleDismiss} aria-label="Dismiss">
            ✕
          </button>
        </div>
      </div>

      {/* iOS guide modal */}
      {showIOSGuide && (
        <div style={styles.modalOverlay} onClick={() => setShowIOSGuide(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>Install on iPhone / iPad</div>
            <ol style={styles.steps}>
              <li style={styles.step}>
                <span style={styles.stepNum}>1</span>
                <span>Tap the <strong>Share</strong> button <span style={styles.sfSymbol}>⎙</span> at the bottom of Safari</span>
              </li>
              <li style={styles.step}>
                <span style={styles.stepNum}>2</span>
                <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
              </li>
              <li style={styles.step}>
                <span style={styles.stepNum}>3</span>
                <span>Tap <strong>Add</strong> — the app icon will appear on your home screen</span>
              </li>
            </ol>
            <button style={styles.closeBtn} onClick={() => { setShowIOSGuide(false); setShowBanner(false); }}>
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#0f2540',
    borderTop: '1px solid rgba(229,184,63,0.3)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    zIndex: 9999,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
  },
  title: {
    color: '#f0f4f8',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
  },
  subtitle: {
    color: 'rgba(240,244,248,0.6)',
    fontSize: 12,
    marginTop: 2,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  installBtn: {
    background: '#E5B83F',
    color: '#0f2540',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dismissBtn: {
    background: 'transparent',
    color: 'rgba(240,244,248,0.5)',
    border: 'none',
    padding: '8px',
    fontSize: 16,
    cursor: 'pointer',
    lineHeight: 1,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '0 0 20px 0',
  },
  modal: {
    background: '#0f2540',
    border: '1px solid rgba(229,184,63,0.3)',
    borderRadius: '20px 20px 16px 16px',
    padding: '28px 24px 24px',
    maxWidth: 420,
    width: '100%',
    margin: '0 16px',
  },
  modalTitle: {
    color: '#E5B83F',
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 20,
    textAlign: 'center',
  },
  steps: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 24px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    color: '#f0f4f8',
    fontSize: 15,
    lineHeight: 1.5,
  },
  stepNum: {
    background: '#E5B83F',
    color: '#0f2540',
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
    marginTop: 1,
  },
  sfSymbol: {
    fontSize: 18,
    verticalAlign: 'middle',
  },
  closeBtn: {
    width: '100%',
    background: '#E5B83F',
    color: '#0f2540',
    border: 'none',
    borderRadius: 10,
    padding: '13px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
